import { useMemo } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { GetDataWithCleanedDates } from "shared/helpers/DateHelpers";
import axios from "axios";
import { GetCustomError } from "shared/helpers/AxiosHelpers";
import { useOktaHelpers } from "shared/hooks/useOktaHelpers";
import { LogTrace } from "components/AppInsights/TelemetryService";

import { useQuery } from "@tanstack/react-query";

export function useFetchServerGridData(
  apiGetEndpoint,
  apiSubmittedFormData,
  serverGridDataState,
  onFailureCallback,
  setServerGridParentData,
  useQueryOptions = null,
  apiPostEndpoint = null
) {
  const { oktaAuth } = useOktaAuth();
  const { invalidTokenOnErrorHandler } = useOktaHelpers();

  const dataKey = "useFetchServerGridDataKey";

  const apiParameters = useMemo(() => {
    if (!serverGridDataState) return {};
    if (!apiSubmittedFormData) return {};

    // Always start with serverGridDataState which contains skip, take, sort, filter
    let parameters = { ...serverGridDataState };

    // Handle different parameter structures for GET vs POST endpoints
    const usePostEndpoint = apiPostEndpoint && apiPostEndpoint.trim() !== "";

    if (usePostEndpoint) {
      // For POST endpoints, preserve the structured format (e.g., Report object)
      // while ensuring grid state parameters (skip, take, sort, filter) are at root level
      parameters = { ...parameters, ...apiSubmittedFormData };

      //NOTE: Ensure all numeric filter values are converted to strings for POST requests
      if (parameters.filter && parameters.filter.filters) {
        parameters.filter.filters = parameters.filter.filters.map(filter => ({
          ...filter,
          value: (filter.value !== null && filter.value !== undefined && typeof filter.value === "number") ? String(filter.value) : filter.value
        }));
      }

    } else {
      // For GET endpoints, merge all properties at the root level (original behavior)
      for (const [key, value] of Object.entries(apiSubmittedFormData)) {
        parameters[key] = value;
      }
    }

    return parameters;
  }, [serverGridDataState, apiSubmittedFormData, apiPostEndpoint]);

  function ProcessedServerGridData(response) {
    let data = [];

    //if the response comes from GridDataProcessor, the data will be in a sub property called gridData, and also Total, and FieldTotals
    // FYI: Here is the model from GridDataProcessor:  { GridData = query.ToList(), Total = total, FieldTotals = calculatedTotals }
    //Otherwise the data will be the response.data and we need to distinguish between the two
    if (response.data.gridData) {
      data = response.data.gridData;
    } else if (response.data) {
      data = response.data;
    }

    let dataWithCleanedDates = GetDataWithCleanedDates(data);

    if (response.data.total) {
      dataWithCleanedDates.total = response.data.total;
    }

    //set totals if provided
    if (response.data.fieldTotals) {
      dataWithCleanedDates.fieldTotals = response.data.fieldTotals;
    }

    //call setServerGridParentData if it is provided
    //if (setServerGridParentData) setServerGridParentData(dataWithCleanedDates);

    return dataWithCleanedDates;
  }

  async function GetServerGridData() {
    //if api paramss are null or empty return empty array, no need to make a call
    if (!apiParameters || Object.keys(apiParameters).length === 0) {
      return [];
    }

    let accessToken = oktaAuth.getAccessToken();

    // Determine which endpoint and method to use
    const usePostEndpoint = apiPostEndpoint && apiPostEndpoint.trim() !== "";
    const endpoint = usePostEndpoint ? apiPostEndpoint : apiGetEndpoint;

    if (usePostEndpoint) {
      LogTrace(
        "useFetchServerGridData hook is called: " + JSON.stringify(apiParameters),
        {
          componentName: "useFetchServerGridData",
          apiEndpoint: apiPostEndpoint,
        },
        1,
      );
    }

    var result = await axios({
      method: usePostEndpoint ? 'POST' : 'GET',
      url: endpoint,
      ...(usePostEndpoint
        ? { data: apiParameters }
        : { params: apiParameters }
      ),
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((response) => {
        let cleanedData = ProcessedServerGridData(response);

        // if (setServerGridParentData) setServerGridParentData(cleanedData);

        return cleanedData;
      })
      .catch((err) => {
        // console.error(err);

        //will check if the token is valid and redirect to login page if not
        invalidTokenOnErrorHandler(err);

        if (onFailureCallback) onFailureCallback(err);

        let thisError = GetCustomError(err);

        throw thisError;
      });

    return result;
  }

  function IsEnabled() {
    // Check if either GET or POST endpoint is provided
    const hasValidEndpoint = apiGetEndpoint || (apiPostEndpoint && apiPostEndpoint.trim() !== "");
    if (!hasValidEndpoint) return false;

    if (!apiSubmittedFormData) return false;

    if (!serverGridDataState) return false;

    return true;
  }

  return useQuery({
    queryKey: [dataKey, apiGetEndpoint, apiPostEndpoint, apiParameters],
    queryFn: async () => await GetServerGridData(),
    enabled: IsEnabled,
    refetchOnWindowFocus:
      useQueryOptions !== null && useQueryOptions.refetchOnWindowFocus !== null
        ? useQueryOptions.refetchOnWindowFocus
        : true,
  });
}
